import { Defect } from '@/types/Defecten';
import { isDateEarlier, isDateLater } from '@/utils/dateUtils';

import { fixDefect } from './defect';
import { defectSorter, defectTimeSorter } from './defectSorter';

/**
 * Group defecten on eenheidsNummer.
 * Om het juiste samegevoegde defect weer
 * te geven worden de defecten gesorteerd.
 * @param ungroupedDefecten Defect[]
 * @returns
 */
export function groupDefectsByEenheid(ungroupedDefecten: Defect[]): [string, Defect[]][] {
  ungroupedDefecten.sort(defectTimeSorter);
  return Array.from(Map.groupBy(ungroupedDefecten, (defect) => defect.eenheidNummer).entries()).map<[string, Defect[]]>(
    ([eenheidsNummer, defecten]) => {
      defecten = defecten.map(fixDefect);
      defecten.sort(defectSorter);
      return [eenheidsNummer, defecten];
    },
  );
}

export type DefectGroupedByEenheid = Defect & {
  /**
   * The original defect ids.
   * Optional param ids;
   */
  ids?: number[];
};

/**
 * Group defecten by eenheidsNummer, add the original defect ids.
 * De functie geeft 1 uniek defect per eenheid terug.
 * Om de juiste 'staat' van de eenheid te tonen, bevat dit defect data van de andere gerelateerde defecten.
 *
 * Zoals:
 * nietMeerInzetbaarVanaf - Bevat het vroegste moment van alle defecten voor desbetreffende eenheid.
 * verwachtGereed - Bevat het laatste moment van alle defecten voor desbetreffende eenheid.
 *
 * @param ungroupedDefecten
 * @returns
 */
export function groupDefectsByEenheidLmm(ungroupedDefecten: Defect[]): DefectGroupedByEenheid[] {
  return groupDefectsByEenheid(ungroupedDefecten).map((defEntry) => {
    const defecten = defEntry[1];
    const { nietMeerInzetbaarVanaf, verwachtGereed } = defecten.reduce(
      (obj, defect) => {
        obj.nietMeerInzetbaarVanaf = isDateEarlier(defect.nietMeerInzetbaarVanaf, obj.nietMeerInzetbaarVanaf)
          ? defect.nietMeerInzetbaarVanaf
          : obj.nietMeerInzetbaarVanaf;

        obj.verwachtGereed = isDateLater(defect?.verwachtGereed ?? null, obj.verwachtGereed)
          ? (defect?.verwachtGereed ?? null)
          : obj.verwachtGereed;
        return obj;
      },
      { nietMeerInzetbaarVanaf: null, verwachtGereed: null } as {
        nietMeerInzetbaarVanaf: string | null;
        verwachtGereed: string | null;
      },
    );
    return {
      ...defecten[0],
      nietMeerInzetbaarVanaf,
      verwachtGereed,
      ids: [...defEntry[1].map((def) => def.id)],
    };
  });
}
